/**
 * Admin Financials extension hooks.
 *
 * Hits /api/admin-ops/users/:userId/financials/* — the manual ledger
 * endpoints used by the admin User Details page (Financials tab) to record
 * committee-fronted entry fees, manual refunds, and arbitrary adjustments.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Types ---
export type AdjustmentType = 'charge' | 'refund' | 'adjustment' | 'comp' | 'credit';
export type AdjustmentDirection = 'debit' | 'credit';

export interface AccountAdjustment {
  id: string;
  user_profile_id: string;
  admin_user_profile_id: string | null;
  organization_id: string | null;
  event_registration_id: string | null;
  payment_id: string | null;
  type: AdjustmentType;
  amount: number;
  currency: string;
  direction: AdjustmentDirection;
  reason: string;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  // Set once a Stripe payment settles this entry. A settled entry is locked
  // against deletion (the server returns 409); the UI hides the delete action.
  settled_at?: string | null;
  settled_by_payment_id?: string | null;
  /** True on a debit charge that blocks new event entries for its org until
   *  paid. Always false on credits. */
  blocks_event_entry?: boolean;
}

export interface AdjustmentsSummary {
  totalDebits: number;
  totalCredits: number;
  netManualBalance: number;
  byType: Record<string, { debit: number; credit: number; count: number }>;
  count: number;
}

export interface CreateAdjustmentInput {
  type: AdjustmentType;
  amount: number;
  currency?: string;
  direction?: AdjustmentDirection;
  reason: string;
  notes?: string;
  organizationId?: string;
  eventRegistrationId?: string;
  paymentId?: string;
  /** Charge-only: block new event entries for the org until paid (e.g. a
   *  fine). Ignored server-side for credit-direction entries. */
  blocksEventEntry?: boolean;
}

// --- Query Key Factory ---
export const adminFinancialsKeys = {
  all: ['admin-ops', 'financials'] as const,
  forUser: (userId: string) => [...adminFinancialsKeys.all, userId] as const,
  list: (userId: string, orgId?: string) =>
    [...adminFinancialsKeys.forUser(userId), 'list', orgId ?? 'all'] as const,
  summary: (userId: string, orgId?: string) =>
    [...adminFinancialsKeys.forUser(userId), 'summary', orgId ?? 'all'] as const,
};

// --- Query Hooks ---
export function useAdminUserAdjustments(
  userId: string | null | undefined,
  orgId?: string,
) {
  return useQuery({
    queryKey: adminFinancialsKeys.list(userId ?? '', orgId),
    enabled: !!userId,
    queryFn: () => {
      const qs = orgId ? `?organizationId=${orgId}` : '';
      return httpClient.get<{ adjustments: AccountAdjustment[] }>(
        `/admin-ops/users/${userId}/financials/adjustments${qs}`,
      );
    },
  });
}

export function useAdminUserAdjustmentsSummary(
  userId: string | null | undefined,
  orgId?: string,
) {
  return useQuery({
    queryKey: adminFinancialsKeys.summary(userId ?? '', orgId),
    enabled: !!userId,
    queryFn: () => {
      const qs = orgId ? `?organizationId=${orgId}` : '';
      return httpClient.get<AdjustmentsSummary>(
        `/admin-ops/users/${userId}/financials/summary${qs}`,
      );
    },
  });
}

// --- Mutation Hook ---
export function useCreateAdminAdjustment(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAdjustmentInput) =>
      httpClient.post<AccountAdjustment>(
        `/admin-ops/users/${userId}/financials/adjustments`,
        data,
      ),
    onSuccess: () => {
      // Invalidate the entire financials subtree for this user so both the
      // ledger list and any rolled-up summary card refetch — they're
      // logically a single view.
      qc.invalidateQueries({ queryKey: adminFinancialsKeys.forUser(userId) });
      // Also refresh the shared transaction-history feed — it now powers the
      // admin Financials tab display (and the contestant's own Profile), so a
      // recorded entry / refund must appear there immediately too.
      qc.invalidateQueries({ queryKey: ['transaction-history'] });
    },
  });
}

/**
 * Hard-delete a manual ledger entry. Hits the admin-gated
 * DELETE /admin-ops/users/:userId/financials/adjustments/:adjustmentId.
 * The backend blocks deletion of settled entries (paid off by a Stripe
 * payment) with a 409. On success we invalidate the user's financials
 * subtree so the ledger list and the rolled-up summary both refetch.
 */
export function useDeleteAdminAdjustment(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (adjustmentId: string) =>
      httpClient.delete<{ success: boolean; deleted: AccountAdjustment }>(
        `/admin-ops/users/${userId}/financials/adjustments/${adjustmentId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminFinancialsKeys.forUser(userId) });
      // Also refresh the shared transaction-history feed — it now powers the
      // admin Financials tab display (and the contestant's own Profile), so a
      // recorded entry / refund must appear there immediately too.
      qc.invalidateQueries({ queryKey: ['transaction-history'] });
    },
  });
}

// ─── Stripe payments + refunds (admin) ─────────────────────────────────────
//
// Surfaces two extra slices alongside the manual ledger so the admin
// Financials tab can show a unified financial-activity timeline AND drive
// the Refund-via-Stripe modal off live `refundable_amount` (= captured -
// completed refunds, computed server-side in one round-trip).

export interface StripePaymentRow {
  id: string;
  user_profile_id: string;
  provider: string;
  provider_payment_id: string | null;
  provider_order_id: string | null;
  amount: number;
  currency: string;
  payment_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  metadata: Record<string, unknown>;
  amount_refunded: number;
  refundable_amount: number;
}

export interface StripeRefundRow {
  id: string;
  payment_id: string;
  provider_refund_id: string | null;
  amount: number;
  /**
   * Refunds inherit currency from the originating payments row. Optional
   * here because the admin endpoint doesn't denormalize it (would require
   * a join); UI defaults to USD for display when absent.
   */
  currency?: string;
  reason: string | null;
  status: string;
  completed_at: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
}

export const adminStripeFinancialsKeys = {
  payments: (userId: string) =>
    [...adminFinancialsKeys.forUser(userId), 'stripe-payments'] as const,
  refunds: (userId: string) =>
    [...adminFinancialsKeys.forUser(userId), 'stripe-refunds'] as const,
};

export function useAdminUserStripePayments(userId: string | null | undefined) {
  return useQuery({
    queryKey: adminStripeFinancialsKeys.payments(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      httpClient.get<{ payments: StripePaymentRow[] }>(
        `/admin-ops/users/${userId}/financials/stripe-payments`,
      ),
  });
}

export function useAdminUserStripeRefunds(userId: string | null | undefined) {
  return useQuery({
    queryKey: adminStripeFinancialsKeys.refunds(userId ?? ''),
    enabled: !!userId,
    queryFn: () =>
      httpClient.get<{ refunds: StripeRefundRow[] }>(
        `/admin-ops/users/${userId}/financials/stripe-refunds`,
      ),
  });
}

export interface CreateStripeRefundInput {
  paymentId: string;
  amount: number;
  reason?: string;
}

/**
 * Issue a real Stripe refund against a prior payment. Hits the existing
 * admin-gated `/payment-ops/:paymentId/refund` endpoint, which validates
 * the requested amount against Stripe's authoritative captured-charge
 * ceiling before issuing — local-row drift can't over-refund. On success
 * the `charge.refunded` webhook reconciles `payments.status` and inserts
 * the `payment_refunds` row within seconds; we invalidate the user's
 * financials subtree so both queries refetch.
 */
export function useCreateAdminStripeRefund(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, amount, reason }: CreateStripeRefundInput) =>
      httpClient.post<{ refund: { id: string; amount: number; status: string } }>(
        `/payment-ops/${paymentId}/refund`,
        { amount, reason },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminFinancialsKeys.forUser(userId) });
      // Also refresh the shared transaction-history feed — it now powers the
      // admin Financials tab display (and the contestant's own Profile), so a
      // recorded entry / refund must appear there immediately too.
      qc.invalidateQueries({ queryKey: ['transaction-history'] });
    },
  });
}
